import { api } from "@/lib/api/client";

export interface UsageTotals {
  responses: number;
  conversations: number;
  users: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost_usd: number;
  avg_latency_ms: number;
  p95_latency_ms: number;
}

export interface UsageDailyPoint {
  day: string;
  responses: number;
  cost_usd: number;
  input_tokens: number;
  output_tokens: number;
}

export interface UsageByUserRow {
  user_id: string;
  email: string;
  display_name: string;
  responses: number;
  cost_usd: number;
  input_tokens: number;
  output_tokens: number;
  last_active_at: string | null;
}

export interface UsageByModelRow {
  provider: string;
  model: string;
  calls: number;
  cost_usd: number;
  input_tokens: number;
  output_tokens: number;
}

export interface UsageSummary {
  totals: UsageTotals;
  daily: UsageDailyPoint[];
  by_user: UsageByUserRow[];
  by_model: UsageByModelRow[];
}

export function fetchUsageSummary(days = 14) {
  return api.get<UsageSummary>(`/admin/usage/summary?days=${days}`);
}

export type AuditStatusFilter = "pending" | "reviewed" | "all";

export interface AuditQueueItem {
  response_id: string;
  conversation_id: string;
  user_email: string;
  user_display_name: string;
  query_text: string;
  answer_excerpt: string;
  cited_concepto_count: number;
  total_cost_usd: number;
  latency_ms: number;
  created_at: string;
  review_status: "pending" | "reviewed";
}

export function fetchAuditQueue(status: AuditStatusFilter = "pending", limit = 50) {
  return api.get<AuditQueueItem[]>(`/admin/audit/queue?status_filter=${status}&limit=${limit}`);
}

export interface AuditReviewRead {
  id: string;
  response_id: string;
  reviewer_id: string;
  verdict: "correct" | "partial" | "incorrect" | "hallucination";
  severity: "low" | "medium" | "high";
  categories: string[];
  comments: string | null;
  corrected_response_json: Record<string, unknown> | null;
  created_at: string;
}

export interface AuditResponseDetail {
  response_id: string;
  conversation_id: string;
  user_id: string;
  user_email: string;
  user_display_name: string;
  query_text: string;
  query_filters: Record<string, unknown>;
  agent_steps: Array<Record<string, unknown>>;
  response_ui: Record<string, unknown>;
  cited_concepto_ids: number[];
  cited_uuids: string[];
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost_usd: number;
  latency_ms: number;
  created_at: string;
  review: AuditReviewRead | null;
}

export function fetchAuditResponseDetail(responseId: string) {
  return api.get<AuditResponseDetail>(`/admin/audit/responses/${responseId}`);
}

export interface AuditReviewSubmission {
  verdict: AuditReviewRead["verdict"];
  severity: AuditReviewRead["severity"];
  categories: string[];
  comments?: string;
  corrected_response?: Record<string, unknown> | null;
}

export function submitAuditReview(responseId: string, payload: AuditReviewSubmission) {
  return api.post<AuditReviewRead>(`/admin/audit/responses/${responseId}/review`, payload);
}

export interface DataSourceFreshness {
  data_source_id: string;
  name: string;
  family: string;
  enabled: boolean;
  last_ingest_at: string | null;
  rows_in_canonical: number;
  last_concepto_id: number | null;
  minutes_since_last_ingest: number | null;
  last_error: string | null;
}

export function fetchFreshness() {
  return api.get<DataSourceFreshness[]>("/admin/data-sources/freshness");
}
